// Reduce a gene's GenCC assertions to what a Clinical Insights ROW can show.
// TGCLOUD-1794 Phase 3.
//
// Pure, so the rules are asserted directly rather than through a render.
//
// The unit of a GenCC assertion is a (gene, disease) PAIR. A Clinical Insights
// row is a single entity, so the pair is formed differently depending on which:
//
//   disease row — the pairs are (each gene the patient holds, THIS disease).
//                 That is the clinically pointed claim: "this patient carries
//                 BRCA1, and BRCA1–this disease is Definitive".
//   gene row    — the pairs are (THIS gene, each disease GenCC grades it against).
//
// Nothing is aggregated into a single grade for the row. 39.5% of pairs carry
// more than one distinct classification and 9.7% straddle strong and weak
// evidence; reducing that to one verdict would manufacture a consensus that
// does not exist, which is the rule written down at
// src/util/variantPathogenicity.js:8-41.

import {
  GENE_VALIDITY_STATE,
  buildGeneValidity,
  geneLookupKey
} from './genotypes-menu/geneValidity';

/**
 * Grades for one (gene, disease) pair, from a batched by-genes response.
 *
 * @returns {{state: string, gene: string, submitters: object[]}|null}
 *          null when the gene is not one we asked about.
 */
export function pairGrade(batch, geneSymbol, diseaseId) {
  const gene = String(geneSymbol || '').trim();
  if (!gene) return null;

  // A gene that is not an approved HGNC symbol was never looked up, so it has
  // no verdict — and must not borrow "not curated" from one.
  if (!geneLookupKey({ symbol: gene })) {
    return {
      state: GENE_VALIDITY_STATE.UNIDENTIFIED_GENE,
      gene,
      submitters: []
    };
  }
  if (!batch || batch.available !== true) {
    return { state: GENE_VALIDITY_STATE.UNAVAILABLE, gene, submitters: [] };
  }

  const all = batch.byGene?.[gene];
  if (!Array.isArray(all)) {
    // Asked for, but absent from the response: treat as unavailable rather than
    // as "this gene has no curated evidence".
    return { state: GENE_VALIDITY_STATE.UNAVAILABLE, gene, submitters: [] };
  }

  const scoped = diseaseId
    ? all.filter((a) => {
        const d = a?.disease || {};
        return (
          d.submittedAs === diseaseId ||
          d.mondo === diseaseId ||
          `${d.source}:${d.code}` === diseaseId
        );
      })
    : all;

  const model = buildGeneValidity({
    available: true,
    datasetVersion: batch.datasetVersion,
    assertions: scoped
  });

  return {
    state: model.state,
    gene,
    // One entry per submitter, never collapsed.
    submitters: model.diseases.flatMap((d) => d.submitters),
    // Read from the GROUPS, not from diseases[], so this agrees with
    // `state` on the same object and with the genotype record's own block.
    // `state` is derived from diseaseGroups (TGCLOUD-1794 Phase 4), and on 84
    // genes a disagreement is only visible once GenCC's disease equivalents are
    // grouped — ANG is ClinGen saying Limited against three labs'
    // Definitive/Strong, split across two curies that share one MONDO. Leaving
    // this on diseases[] would let one row say "submitters disagree" in the
    // genotype record and stay silent in Clinical Insights, in the same session
    // on the same data.
    conflicting: model.diseaseGroups.some((g) => g.conflicting)
  };
}

/**
 * The gene symbols a set of Clinical Insights rows needs grades for.
 *
 * Disease rows contribute the genes the PATIENT holds — not every associated
 * gene. A well-connected disease can carry 113 of them, and grading genes the
 * patient does not have says nothing about this patient.
 */
export function genesToGrade({
  diseaseRows = [],
  geneRows = [],
  patientGeneSet
} = {}) {
  const out = new Set();
  for (const r of diseaseRows) {
    for (const g of r?.associatedGenes || []) {
      if (!patientGeneSet || patientGeneSet.has?.(String(g).toUpperCase())) {
        out.add(String(g).trim());
      }
    }
  }
  for (const r of geneRows) {
    // Gene rows are labelled "BRCA1" or "BRCA1 (NCBIGene:672)".
    const sym = String(r?.name || r?.id || '')
      .split(' (')[0]
      .trim();
    if (sym) out.add(sym);
  }
  out.delete('');
  return [...out];
}
